import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Input } from "./ui";

// Fachliche Sanity-Grenzen fürs ETW-Screening — Werte außerhalb gelten als
// Datenmüll und dürfen die Slider-Endpunkte nicht verziehen (siehe sizeBounds
// in Listings.jsx). Mieter-spezifische Seiten nutzen eigene, engere Grenzen.
export const SIZE_SANITY = { min: 10, max: 400 };           // m²
export const PRICE_SANITY = { min: 10_000, max: 3_000_000 }; // €

// 99. Perzentil eines aufsteigend sortierten Arrays (n ≥ 1) — robustes
// oberes Regler-Ende statt Max, damit legitime Ausreißer den Slider nicht
// sprengen.
export function percentile99(sorted) {
  return sorted[Math.min(sorted.length - 1, Math.ceil(sorted.length * 0.99) - 1)];
}

export function FilterCell({ label, children, last }) {
  return (
    <div className={`col-span-6 md:col-span-2 p-4 ${last ? "" : "border-r border-border"}`}>
      <div className="overline mb-1.5">{label}</div>
      {children}
    </div>
  );
}

export function RangeCell({
  label,
  minValue,
  maxValue,
  min,
  max,
  step,
  testidMin,
  testidMax,
  onMinChange,
  onMaxChange,
  minLabel,
  maxLabel,
  last,
}) {
  return (
    <div className={`p-4 border-b border-border md:border-b-0 ${last ? "" : "md:border-r"}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="overline">{label}</div>
        <div className="text-[10px] font-mono text-white tabular-nums">{minLabel} - {maxLabel}</div>
      </div>
      <div className="space-y-3">
        <RangeThumb
          label="Min"
          testid={testidMin}
          min={min}
          max={maxValue}
          step={step}
          value={minValue}
          onChange={(next) => {
            onMinChange(next);
            if (next > maxValue) onMaxChange(next);
          }}
        />
        <RangeThumb
          label="Max"
          testid={testidMax}
          min={minValue}
          max={max}
          step={step}
          value={maxValue}
          onChange={(next) => {
            onMaxChange(next);
            if (next < minValue) onMinChange(next);
          }}
        />
      </div>
    </div>
  );
}

export function RangeThumb({ label, testid, min, max, step, value, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="text-[10px] font-mono uppercase tracking-widest2 text-text-muted">{label}</div>
        <div className="text-[10px] font-mono text-white tabular-nums">{value}</div>
      </div>
      <input
        data-testid={testid}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-white"
      />
    </div>
  );
}

export function Metric({ label, value, accent = "white" }) {
  const cls = { white: "text-white", green: "text-signal-green", yellow: "text-signal-yellow", red: "text-signal-red" }[accent];
  return (
    <div>
      <div className="overline">{label}</div>
      <div className={`num text-base mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

export function MarketMultiSelect({ options, value, onChange, query, onQueryChange, t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (code) =>
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);

  const label =
    value.length === 0
      ? t("common.all")
      : value.length === 1
      ? (options.find((o) => o.value === value[0])?.label ?? value[0])
      : `${value.length} ${t("listings.marketsSelected")}`;

  return (
    <div className="relative w-full" ref={ref}>
      <button
        type="button"
        data-testid="filter-market"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between bg-bg border border-border text-xs font-mono px-2 py-1.5 text-white"
      >
        <span className="truncate">{label}</span>
        <ChevronDown size={10} className="ml-1 shrink-0 text-text-muted" />
      </button>
      {open && (
        <div data-testid="market-dropdown" className="absolute z-20 top-full left-0 right-0 bg-bg border border-border mt-0.5 max-h-52 overflow-y-auto shadow-lg">
          <div className="p-2 border-b border-border">
            <Input
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search markets or state, e.g. NÖ"
              className="!py-1.5 !text-xs"
              data-testid="market-search"
            />
          </div>
          <button
            type="button"
            onClick={() => onChange([])}
            className={`w-full text-left px-2 py-1.5 text-xs font-mono hover:bg-surface ${value.length === 0 ? "text-signal-green" : "text-text-muted"}`}
          >
            {t("common.all")}
          </button>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              data-testid={`market-option-${opt.value}`}
              className={`w-full text-left px-2 py-1.5 text-xs font-mono hover:bg-surface flex items-center gap-2 ${
                value.includes(opt.value) ? "text-signal-green" : "text-text-muted"
              }`}
            >
              <span className={`w-2 h-2 shrink-0 border ${value.includes(opt.value) ? "bg-signal-green border-signal-green" : "border-border"}`} />
              {opt.label}
            </button>
          ))}
          {options.length === 0 && (
            <div className="px-2 py-2 text-[10px] font-mono text-text-muted">
              No markets match the search.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
